/**
 * Скільки треба вдарити: how long a block takes to come apart.
 *
 * "Add durability to blocks - only creative mode should allow to break
 * blocks in one hit. In a normal game it should depend on what you are
 * using. Empty hand can break earth with 4 hits, empty hand can break a
 * chest and any tree block with 8 hits. Hits count only if you hold the
 * button."
 *
 * Until now a block came apart the moment you touched it, and the tools
 * carried the whole weight of "what are you holding": a pick was not faster
 * than a hand, it was the only thing that worked at all. That rule stays -
 * see tools.ts, and the trap it exists to avoid - and this is the other
 * half: what a hand *can* break, it breaks slowly, and the right tool is
 * what makes the difference between chipping and digging.
 *
 * Two numbers and no more. A material says how many blows it takes bare
 * handed; a tool that suits the material divides that. Everything else -
 * which tool suits what, how much better iron is than wood - is a line in a
 * table here, meant to be argued with and changed.
 *
 * Pure: it knows blocks and items by number and has never seen a frame.
 */

import { blockMaterial, type Material } from "./material.js";
import { BlockId, isFlag, FLAG_BASE, FLAG_COUNT } from "./registry.js";
import { PlainItemId, itemTool } from "./items.js";
import { type ToolKind } from "./tools.js";

/**
 * How often a held button lands a blow, in seconds.
 *
 * Just over three a second - the rhythm of somebody working rather than of a
 * machine. It sets every number below in real time: earth at four blows is a
 * second and a bit, a log at eight is two and a half.
 */
export const BLOW_SECONDS = 0.28;

/** More than this in one tick is a tab that was in the background. */
const MOST_PER_TICK = 4;

/**
 * How long a half-dug block remembers being hit after the button comes up.
 *
 * Strictly, "hits count only if you hold the button" - and holding is what a
 * mouse does. A finger cannot: on a phone the hold gesture already means
 * "place", so digging there is a tap, and a tap that forgot everything the
 * moment it ended would make every block unbreakable by finger. Two thirds
 * of a second is a rhythm of tapping and nothing else - stop for a moment
 * and the block is whole again.
 */
export const FORGET_SECONDS = 0.66;

/**
 * Blows with nothing in your hand.
 *
 * Earth at four and wood at eight are the two the game was asked for; the
 * rest sit around them. Stone is not on this table as something you can
 * punch - `requiredTool` refuses it outright - but the number is what a pick
 * divides into, so it is here.
 *
 * Plants come away in one. That is not laziness about the table: pulling up
 * wheat, snapping a branch of leaves or taking a torch off a wall is one
 * motion, and making the first minute of the game a series of four-blow
 * struggles with grass would teach the wrong thing about what is hard.
 */
const BY_HAND: Readonly<Record<Material, number>> = {
  earth: 4,
  wood: 8,
  stone: 12,
  glass: 4,
  plant: 1,
  cloth: 2,
  fluid: 0,
  none: 0,
};

/**
 * The tools that suit each material, best first.
 *
 * Wood has two: a сокира, which is the tool for it, and a шабля, which is a
 * blade and will do at a pinch. They are not equal and the table does not
 * say they are - how much each is worth is the item's own strength below,
 * and an axe is worth more than a sabre on wood the same way iron is worth
 * more than wood.
 *
 * Before the axe existed the шабля was the only thing on this line, because
 * without it wood was eight blows for ever no matter what anybody carried.
 * It stays: a шабля in the hand of somebody who has not made an axe yet
 * should still be better than a fist.
 */
const HELPED_BY: Readonly<Partial<Record<Material, readonly ToolKind[]>>> = {
  earth: ["shovel"],
  stone: ["pickaxe"],
  glass: ["pickaxe"],
  wood: ["axe", "sabre"],
};

/**
 * How much the right tool helps, by what it is made of.
 *
 * Wooden tools halve the work and iron takes a third of it. Read off the
 * item rather than guessed from its name: a table of four entries is
 * cheaper to be sure of than a rule about prefixes.
 */
const STRENGTH: ReadonlyMap<number, number> = new Map<number, number>([
  // Адамантій: the top tier, and the only one that opens a прапор.
  [PlainItemId.AdamantPickaxe, 4],
  [PlainItemId.AdamantSabre, 4],
  // The сокира is the one tool that is better than its tier suggests, and
  // that is the point of it: a wooden axe beats an iron шабля at wood,
  // because one is a wedge for splitting and the other is a blade for a
  // fight. Eight blows on a log becomes three, and two with the iron one.
  [PlainItemId.WoodenAxe, 3],
  [PlainItemId.IronAxe, 4],
  [PlainItemId.WoodenPickaxe, 2],
  [PlainItemId.WoodenShovel, 2],
  [PlainItemId.WoodenSabre, 2],
  [PlainItemId.WoodenHoe, 2],
  [PlainItemId.IronPickaxe, 3],
  [PlainItemId.IronSabre, 3],
  [PlainItemId.LeaderSabre, 3],
]);

/** What this item is worth against the material it suits. 1 is a bare hand. */
export function toolStrength(itemId: number): number {
  return STRENGTH.get(itemId) ?? 1;
}

/**
 * Blows to break this block with this in hand.
 *
 * Creative is one, always: "only creative mode should allow to break blocks
 * in one hit". Somebody laying out a building is not mining, and making them
 * wait two seconds a log would make the mode useless for the one thing it is
 * for.
 */
export function blowsToBreak(
  blockId: number,
  heldItemId: number,
  creative = false,
): number {
  if (UNBREAKABLE.includes(blockId)) return 0;
  if (toolStrength(heldItemId) < requiredStrength(blockId)) return 0;
  const named = BLOWS.get(blockId);
  // Not shortened by creative either: there is no creative on the one map
  // this applies to, and a flag that falls in one hit is not a win.
  if (named !== undefined) return named;
  if (ONE_BLOW.includes(blockId)) return 1;
  const material = blockMaterial(blockId);
  const bare = BY_HAND[material];
  if (bare <= 0) return 0;
  if (creative) return 1;

  const helps = HELPED_BY[material];
  const held = itemTool(heldItemId);
  if (helps === undefined || held === undefined || !helps.includes(held)) {
    return bare;
  }
  return Math.max(1, Math.round(bare / toolStrength(heldItemId)));
}

/**
 * Fixtures that lift off in one blow whatever they are made of.
 *
 * A смолоскип is wood, and eight blows to take a torch off a wall would be
 * absurd: it is hung there, not built in. The same for a sapling, which is
 * a twig in the ground.
 */
/**
 * Blocks nothing takes out, ever.
 *
 * Checked before creative, deliberately. A floor that the person who ticked
 * a box can dig through is not a floor: on «Сучасна війна» the bottom of the
 * world is what stops a team tunnelling under the wall, and a rule with an
 * exception in it would be no rule.
 */
const UNBREAKABLE: readonly number[] = [BlockId.Bedrock];

/**
 * How strong a tool has to be before a block gives anything up.
 *
 * The older rule - `requiredTool` - is about *kind*: stone wants a pick and
 * nothing else will do. This one is about tier, and only two things in the
 * game use it: адамантій, which "можна копати тільки залізним кайлом", and
 * the прапори, which only адамантій opens. Everything else answers nought
 * and is decided by the kind alone.
 */
export function requiredStrength(blockId: number): number {
  if (blockId === BlockId.Adamant) return 3;
  // "Прапор можна зламати тільки кайлом з адамантію" - the top of the tree,
  // and the reason anybody goes down to the floor of the world at all.
  if (isFlag(blockId)) return 4;
  return 0;
}

/**
 * Blocks that answer for themselves rather than through their material.
 *
 * One entry, and it is the point of a whole map: a прапор is cloth, and by
 * the material table cloth is two blows. Winning «Сучасна війна» should not
 * be half a second of holding a button in front of something nobody is
 * guarding - it is twenty blows, which is five seconds of standing in the
 * open in the middle of somebody else's quarter.
 */
const BLOWS: ReadonlyMap<number, number> = new Map<number, number>([
  ...Array.from({ length: FLAG_COUNT }, (_, i) =>
    [FLAG_BASE + i, 20] as [number, number]),
  /*
   * Шопопало-блок: four, whatever is in your hand.
   *
   * "Після 4 ударів по блоку з нього може випасти будь-який з існуючих
   * блоків." Four is the whole rhythm of that map, so it is four with bare
   * hands, four with an iron кайло and four in режим творця - a source you
   * can empty at one click a second is not a game about one block.
   */
  [BlockId.Random, 4],
]);

const ONE_BLOW: readonly number[] = [
  BlockId.Torch, BlockId.OakSapling, BlockId.AcaciaSapling,
  BlockId.PineSapling,
];

/** What one tick of holding the button did. */
export interface Swing {
  /** Blows that landed this tick. Nought most ticks: they are paced. */
  readonly blows: number;
  /** The last of them finished the block off. */
  readonly done: boolean;
}

/**
 * A block being worked at, and how far in.
 *
 * Holds the one thing that cannot be recomputed from the world: how many
 * blows have landed on *this* cell while the button has been held down
 * without a break. Let go, look away, or take a step that changes what you
 * are aiming at, and it is all lost - "hits count only if you hold the
 * button".
 */
export class Digging {
  private at: { x: number; y: number; z: number } | null = null;
  private needed = 0;
  private landed = 0;
  private wait = 0;
  private idle = 0;

  /** The cell being worked at, or null when nothing is. */
  get cell(): { x: number; y: number; z: number } | null {
    return this.at;
  }

  /** How far through it, 0 to 1. What the cracks are drawn from. */
  get progress(): number {
    if (this.at === null || this.needed <= 0) return 0;
    return Math.min(1, this.landed / this.needed);
  }

  /** Forget it: the button came up, or the block is gone. */
  stop(): void {
    this.at = null;
    this.needed = 0;
    this.landed = 0;
    this.wait = 0;
    this.idle = 0;
  }

  /**
   * A tick of holding the button while aiming at `cell`.
   *
   * The first blow lands at once rather than one beat later: a click that
   * does nothing until a quarter of a second has passed feels like a click
   * the game missed.
   */
  swing(
    dt: number,
    holding: boolean,
    cell: { x: number; y: number; z: number } | null,
    needed: number,
  ): Swing {
    if (!holding || cell === null || needed <= 0) {
      // Not let go of so much as put down: a tap's work is kept for a
      // moment, so tapping gets through a block and idling does not.
      this.idle += dt;
      if (this.idle > FORGET_SECONDS) this.stop();
      return { blows: 0, done: false };
    }
    this.idle = 0;

    if (this.at === null ||
      this.at.x !== cell.x || this.at.y !== cell.y || this.at.z !== cell.z) {
      this.at = { x: cell.x, y: cell.y, z: cell.z };
      this.landed = 0;
      this.wait = 0;
    }
    // Read every tick, not once: swapping to a pick mid-hole has to count.
    this.needed = needed;

    this.wait -= dt;
    let blows = 0;
    while (this.wait <= 0 && blows < MOST_PER_TICK) {
      blows++;
      this.landed++;
      this.wait += BLOW_SECONDS;
      if (this.landed >= this.needed) {
        this.stop();
        return { blows, done: true };
      }
    }
    return { blows, done: false };
  }
}
